import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

// Get token from localStorage
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
};

// TypeScript interfaces for legacy import data types

export interface LegacyAnalysisResult {
  totalClaimants: number;
  totalClaims: number;
  dateRange: {
    earliest: string;
    latest: string;
  };
  claimsByStatus: {
    [status: string]: number;
  };
  claimsByLender: {
    [lender: string]: number;
  };
  fileInfo: {
    filename: string;
    fileSize: number;
    uploadedAt: string;
  };
  warnings?: string[];
  errors?: string[];
}

export interface LegacyImportJob {
  jobId: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  totalItems: number;
  processedItems: number;
  successCount: number;
  errorCount: number;
  startedAt: string;
  completedAt?: string;
  errors?: Array<{
    item: string;
    error: string;
  }>;
}

export interface LegacyImportProgress {
  jobId: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress: number; // 0-100
  currentStep: string;
  totalItems: number;
  processedItems: number;
  successCount: number;
  errorCount: number;
  estimatedTimeRemaining?: number; // seconds
  errors?: string[];
}

export interface LegacyClaimant {
  id: string;
  legacyId: string;
  firstName: string;
  lastName: string;
  email?: string;
  phoneNumber?: string;
  addressLine1?: string;
  addressLine2?: string;
  town?: string;
  county?: string;
  postcode?: string;
  dateOfBirth?: string;
  importedAt: string;
  importJobId: string;
  claims?: LegacyClaim[];
  claimsCount?: number;
  audited?: boolean;
  auditDate?: string;
  auditedBy?: string;
  audits?: LegacyClaimantAudit[];
  // Document URLs
  clientCareLetterUrl?: string;
  identificationUrl?: string;
  formOfAuthorityUrl?: string;
  letterOfAuthorityUrl?: string;
  letterOfComplaintUrl?: string;
  // Correspondence
  correspondence?: LegacyCorrespondence[];
}

export interface LegacyClaimantsPage {
  items: LegacyClaimant[];
  page: number;
  pageSize: number;
  total: number;
  hasMore: boolean;
}

export interface LegacyClaim {
  id: string;
  legacyId: string;
  claimantId: string;
  lender: string;
  agreementNumber: string;
  status: string;
  startDate?: string;
  endDate?: string;
  creditAmount?: number;
  balance?: number;
  monthlyPayment?: number;
  accountType?: string;
  importedAt: string;
  importJobId: string;
  mapped?: boolean;
  mappedClaimId?: string;
  mappedAt?: string;
  correspondence?: LegacyCorrespondence[];
}

export interface LegacyCorrespondence {
  id: string;
  type: 'email' | 'letter' | 'phone' | 'fax' | 'document' | 'other';
  documentType?: string;
  direction: 'sent' | 'received' | 'incoming' | 'outgoing'; // Added incoming/outgoing for phone calls
  subject?: string;
  sender?: string;
  recipient?: string;
  date: string;
  content?: string;
  hasAttachments?: boolean;
  // Phone-specific fields
  callDuration?: number; // in seconds
  callAnswered?: boolean;
  callNotes?: string;
  attachmentUrls?: string[];
  attachmentUrl?: string;
  originalFilename?: string;
  uploadedBy?: string;
  createdAt?: string;
  createdBy?: string;
}

export interface LegacyClaimantAudit {
  id: string;
  auditedBy: string;
  auditedAt: string;
  // DBA Section
  dba_signed_and_on_file?: boolean;
  dba_printed_name_signature_match_file?: boolean;
  dba_address_match_file?: boolean;
  dba_terms_of_business_agree_to_dba?: boolean;
  // FOA Section
  foa_signed_and_on_file?: boolean;
  foa_and_dba_signature_match?: boolean;
  // KYC Section
  kyc_credit_check_on_file?: boolean;
  kyc_name_match?: boolean;
  kyc_address_match?: boolean;
  kyc_pep_sanctions_clear?: boolean;
  kyc_electoral_roll_confirmed?: boolean;
  kyc_active_credit_accounts_found?: boolean;
  // Notes
  notes?: string;
}

// API Functions

// 1. Analyze legacy XML file
export const analyzeLegacyXML = async (file: File): Promise<LegacyAnalysisResult> => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await axios.post<LegacyAnalysisResult>(
      `${API_URL}/ui/legacy/analyze`,
      formData,
      { 
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      }
    );
    return response.data;
  } catch (error: any) {
    throw error;
  }
};

// 2. Start legacy import
export const startLegacyImport = async (files: File | File[], options?: {
  validateOnly?: boolean;
  skipDuplicates?: boolean;
  batchSize?: number;
}): Promise<{ jobId: string; message: string; uploadedFiles?: string[]; fileCount?: number }> => {
  try {
    const formData = new FormData();
    
    // Handle both single file and multiple files
    const fileArray = Array.isArray(files) ? files : [files];
    fileArray.forEach(file => {
      formData.append('file', file);
    });
    
    if (options) {
      Object.entries(options).forEach(([key, value]) => {
        if (value !== undefined) {
          formData.append(key, String(value));
        }
      });
    }
    
    const response = await axios.post<{ 
      success: boolean;
      jobId: string; 
      message: string;
      uploadedFiles?: string[];
      fileCount?: number;
    }>(
      `${API_URL}/ui/legacy/import`,
      formData,
      { 
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      }
    );
    return {
      jobId: response.data.jobId,
      message: response.data.message,
      uploadedFiles: response.data.uploadedFiles,
      fileCount: response.data.fileCount
    };
  } catch (error: any) {
    throw error;
  }
};

// 3. Get import progress
export const getImportProgress = async (jobId: string): Promise<LegacyImportProgress> => {
  try {
    const response = await axios.get<{
      success: boolean;
      progress: LegacyImportProgress;
    }>(
      `${API_URL}/ui/legacy/import/${jobId}/progress`,
      { headers: getAuthHeaders() }
    );
    return response.data.progress;
  } catch (error: any) {
    throw error;
  }
};

// 4. Get all import jobs
export const getAllImportJobs = async (): Promise<LegacyImportJob[]> => {
  try {
    const response = await axios.get<{ jobs: LegacyImportJob[] }>(
      `${API_URL}/ui/legacy/import/jobs`,
      { headers: getAuthHeaders() }
    );
    return response.data.jobs;
  } catch (error: any) {
    throw error;
  }
};

// 5. Get legacy claimants
export const getLegacyClaimants = async (
  page: number = 1,
  pageSize: number = 50,
  search?: string
): Promise<LegacyClaimantsPage> => {
  try {
    const params: any = { page, pageSize };
    if (search) params.search = search;
    
    const response = await axios.get<{
      success: boolean;
      claimants: LegacyClaimant[];
      totalCount: number;
      page: number;
      pageSize: number;
    }>(
      `${API_URL}/ui/legacy/claimants`,
      { 
        headers: getAuthHeaders(),
        params
      }
    );
    
    // Transform the response to match the expected format
    const data = response.data;
    return {
      items: data.claimants || [],
      page: data.page,
      pageSize: data.pageSize,
      total: data.totalCount || 0,
      hasMore: (data.page * data.pageSize) < (data.totalCount || 0)
    };
  } catch (error: any) {
    throw error;
  }
};

// 6. Get legacy claimant
export const getLegacyClaimant = async (claimantId: string): Promise<LegacyClaimant> => {
  try {
    const response = await axios.get<{
      success: boolean;
      claimant: LegacyClaimant;
    }>(
      `${API_URL}/ui/legacy/claimants/${claimantId}`,
      { headers: getAuthHeaders() }
    );
    return response.data.claimant;
  } catch (error: any) {
    throw error;
  }
};

// 7. Create claimant audit
export const createClaimantAudit = async (
  claimantId: string,
  auditData: Partial<LegacyClaimantAudit>
): Promise<{ message: string; auditId: string }> => {
  try {
    const response = await axios.post<{ success: boolean; message?: string; auditId: string }>(
      `${API_URL}/ui/legacy/claimants/${claimantId}/audit`,
      auditData,
      { headers: getAuthHeaders() }
    );
    return { 
      message: response.data.message || 'Audit created successfully',
      auditId: response.data.auditId 
    };
  } catch (error: any) {
    throw error;
  }
};

// 8. Get legacy claim
export const getLegacyClaim = async (claimId: string): Promise<LegacyClaim> => {
  try {
    const response = await axios.get<{
      success: boolean;
      claim: LegacyClaim;
    }>(
      `${API_URL}/ui/legacy/claims/${claimId}`,
      { headers: getAuthHeaders() }
    );
    return response.data.claim;
  } catch (error: any) {
    throw error;
  }
};

// 9. Get legacy claim correspondence
export const getLegacyClaimCorrespondence = async (claimId: string): Promise<LegacyCorrespondence[]> => {
  try {
    const response = await axios.get<{
      success: boolean;
      correspondence: LegacyCorrespondence[];
    }>(
      `${API_URL}/ui/legacy/claims/${claimId}/correspondence`,
      { headers: getAuthHeaders() }
    );
    return response.data.correspondence || [];
  } catch (error: any) {
    throw error;
  }
};

// 10. Add legacy claim correspondence
export const addLegacyClaimCorrespondence = async (
  claimId: string, 
  correspondence: Partial<LegacyCorrespondence>
): Promise<LegacyCorrespondence> => {
  try {
    const response = await axios.post<{
      success: boolean;
      correspondence: LegacyCorrespondence;
    }>(
      `${API_URL}/ui/legacy/claims/${claimId}/correspondence`,
      correspondence,
      { headers: getAuthHeaders() }
    );
    return response.data.correspondence;
  } catch (error: any) {
    throw error;
  }
};

// Get legacy claimant correspondence
export const getLegacyClaimantCorrespondence = async (claimantId: string): Promise<LegacyCorrespondence[]> => {
  try {
    const claimant = await getLegacyClaimant(claimantId);
    return claimant.correspondence || [];
  } catch (error: any) {
    throw error;
  }
};

// Upload document for legacy claimant
export const uploadLegacyClaimantDocument = async (
  claimantId: string, 
  file: File, 
  documentType: string = 'correspondence',
  description: string = '',
  claimId?: string
): Promise<{ success: boolean; correspondence: LegacyCorrespondence; savedTo: 'claim' | 'claimant' }> => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('documentType', documentType);
    formData.append('description', description);
    if (claimId) {
      formData.append('claimId', claimId);
    }

    const response = await axios.post<{
      success: boolean;
      message: string;
      correspondence: LegacyCorrespondence;
      savedTo: 'claim' | 'claimant';
    }>(
      `${API_URL}/ui/legacy/claimants/${claimantId}/documents`,
      formData,
      { 
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      }
    );
    
    return response.data;
  } catch (error: any) {
    throw error;
  }
};